import { useQuery } from "@apollo/client";
import Description from "@mui/icons-material/Description";
import { Alert, AlertTitle, Box, Chip, Grid, Skeleton, Stack, Typography } from "@mui/material";
import { ExtractionReviewGrid } from "components/Document/ExtractionReview";
import { DocumentDialogContainer } from "components/Document/shared";
import { FacilityPicker } from "components/FacilityPicker";
import { FormTextFieldSkeleton } from "components/Skeleton";
import { FacilityPickerFragment, WasteKind } from "generated-graphql/graphql";
import { useCallback, useState } from "react";
import { useRetryDocumentExtraction } from "routes/Customer/Documents/hooks/useRetryDocumentExtraction";

import { SHIPPING_DOCUMENT_FILE } from "./billOfLadingApi";
import { BillOfLadingDiscardAction } from "./BillOfLadingDiscardAction";
import {
  BILL_OF_LADING_REVIEW_SCHEMA,
  DECLARED_WASTE_KIND_LABELS,
  declaredKinds,
  GENERATOR_EPA_ID_KEY,
  toExtractionResult,
  toGridExtractionState,
} from "./extractionReview";
import { useApplyReviewedBillOfLading } from "./useApplyReviewedBillOfLading";
import { useBillOfLadingEpaIdIssues } from "./useBillOfLadingEpaIdIssues";
import { useBillOfLadingReading } from "./useBillOfLadingReading";

const WARNING_FIELD_KEYS = [GENERATOR_EPA_ID_KEY];

export type BillOfLadingReviewDialogProps = {
  onClose: () => void;
  /**
   * Document ids to review. Only the first is used: `BillOfLadingUploadDialog`
   * uploads one file per extraction job (see its own doc comment), so this
   * list is never longer than one in practice. The array shape matches
   * `BillOfLadingUploadFlow`'s handoff from the analyzing dialog, which reports
   * a job's succeeded documents as a list regardless of job size.
   */
  documentIds: string[];
  /** The facility confirmed on upload; the picker here still lets it be changed. */
  facilityId: string;
  /** Called with the id of the `WasteManifest` the confirm wrote. */
  onSaved: (wasteManifestId: string) => void;
  /** The document's tenant, for a host outside that tenant: scopes the facility picker to it. */
  tenantId?: string;
  /** Offers "Discard upload" when set; called once the upload is deleted. */
  onDiscarded?: () => void;
};

/**
 * What the reviewer is looking at, before the fields themselves.
 *
 * Deliberately `info` rather than `warning`: this explains, while the grid's own
 * banner is the one that asks for action on low-confidence values. Two warning
 * banners stacked would leave neither meaning anything.
 */
function ReviewIntro({ kinds, loading }: { kinds: WasteKind[]; loading: boolean }) {
  if (loading) {
    return (
      <Grid size={{ xs: 12 }} sx={{ mb: 2 }}>
        <Skeleton variant="rounded" height={96} />
      </Grid>
    );
  }
  return (
    <Grid size={{ xs: 12 }} sx={{ mb: 2 }}>
      <Alert severity="info" icon={<Description fontSize="inherit" />}>
        <AlertTitle>Not an EPA manifest</AlertTitle>
        {/*
         * Phrased as an instruction, matching the manifest review modal's
         * "AI parsed this manifest document. Please review the parsed values
         * and confirm." Deliberately without that modal's "(marked with a
         * sparkle)" clause: this grid draws no such marker, so naming one would
         * send the reader looking for something that is not there.
         */}
        <Typography variant="body2">
          AI read this carrier&apos;s shipping paper. Please review the values below, correct
          anything wrong, then confirm to save it.
        </Typography>
        {kinds.length > 0 && (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            useFlexGap
            flexWrap="wrap"
            mt={1.5}
          >
            <Typography variant="caption" color="text.secondary">
              Declared on the document
            </Typography>
            {kinds.map((kind) => (
              <Chip
                key={kind}
                size="small"
                color="info"
                variant="outlined"
                label={DECLARED_WASTE_KIND_LABELS[kind]}
              />
            ))}
          </Stack>
        )}
      </Alert>
    </Grid>
  );
}

/**
 * The facility picker, kept mounted under a skeleton while the reading loads so
 * it looks up the facility alongside it rather than after.
 */
function FacilitySection({
  loading,
  tenantId,
  value,
  onChange,
  disabled,
  missing,
}: {
  loading: boolean;
  tenantId: string | undefined;
  value: FacilityPickerFragment | string;
  onChange: (facility: FacilityPickerFragment | null) => void;
  disabled: boolean;
  missing: boolean;
}) {
  return (
    <Grid size={{ xs: 12 }} sx={{ mb: 2 }}>
      {loading && <FormTextFieldSkeleton kind="long" />}
      <Box sx={{ display: loading ? "none" : undefined }}>
        <FacilityPicker
          label="Facility"
          defaultSearchTerm={tenantId ? `tenantId:${tenantId}` : ""}
          facilityQueryVariant="waste"
          textFieldProps={{ required: true }}
          value={value}
          onChange={onChange}
          disabled={disabled}
          invalid={false}
          isTouched={false}
          isDirty={false}
          isValidating={false}
        />
        {missing && (
          <Typography variant="caption" color="text.secondary">
            Choose the facility this bill of lading belongs to
          </Typography>
        )}
      </Box>
    </Grid>
  );
}

/** Confirmed: the document is reviewed and its manifest is saved, whatever the reading's own status. */
function isConfirmedReading(
  reading: {
    documentReviewed: boolean;
    savedWasteManifestId?: string | null;
  } | null
) {
  return reading?.documentReviewed === true && reading.savedWasteManifestId != null;
}

/**
 * Re-queue extraction for this document and re-read it when that job finishes.
 *
 * The retry runs as its own job behind the analyzing dialog, so without the
 * re-read the grid keeps showing the candidate that failed: a successful retry
 * would leave a dialog that cannot be committed until it is remounted. Both the
 * reading and the document are re-read, because the grid's failed state comes
 * from the document's own extraction status.
 */
function useRetryAndReread({
  documentId,
  tenantId,
  refetchReading,
  refetchDocument,
}: {
  documentId: string | undefined;
  tenantId: string | null | undefined;
  refetchReading: () => unknown;
  refetchDocument: () => unknown;
}) {
  const onExtractionComplete = useCallback(() => {
    void refetchReading();
    void refetchDocument();
  }, [refetchReading, refetchDocument]);

  const { retryDocumentExtractions } = useRetryDocumentExtraction({ onExtractionComplete });

  return useCallback(() => {
    if (!documentId || !tenantId) return;
    void retryDocumentExtractions(tenantId, [documentId]);
  }, [documentId, tenantId, retryDocumentExtractions]);
}

/**
 * Review a bill of lading's AI reading before writing it as a
 * bill of lading `WasteManifest`.
 *
 * Left pane: a facility picker (decision 4 — confirmed here again, not just
 * on upload) above the shared extraction grid. Right pane: the source PDF,
 * via `DocumentDialogContainer`'s built-in preview column (which is what
 * renders `FullScreenDocumentPreview` under its own full-screen toggle).
 */
export function BillOfLadingReviewDialog({
  onClose,
  documentIds,
  facilityId: initialFacilityId,
  onSaved,
  tenantId,
  onDiscarded,
}: BillOfLadingReviewDialogProps) {
  const documentId = documentIds[0];
  const [facility, setFacility] = useState<FacilityPickerFragment | null>(null);
  const facilityId = facility?.id ?? initialFacilityId;

  const {
    data: fileData,
    loading: fileLoading,
    refetch: refetchDocument,
  } = useQuery(SHIPPING_DOCUMENT_FILE, {
    variables: { id: documentId ?? "" },
    skip: !documentId,
  });
  const document = fileData?.document;

  const {
    reading,
    loading: readingLoading,
    refetch: refetchReading,
  } = useBillOfLadingReading(documentId);

  const isConfirmed = isConfirmedReading(reading);

  const handleRetry = useRetryAndReread({
    documentId,
    tenantId: document?.tenantId,
    refetchReading,
    refetchDocument,
  });

  const { confirm, applying } = useApplyReviewedBillOfLading({
    documentId: documentId ?? "",
    facilityId,
    isConfirmed,
    savedWasteManifestId: reading?.savedWasteManifestId,
    savedWasteManifestVersion: reading?.savedWasteManifestVersion,
    onApplied: onSaved,
  });

  const { status: gridStatus, extractionError: gridError } = toGridExtractionState(
    reading,
    document
  );
  const kinds = declaredKinds(reading);
  const { initialIssues, validate } = useBillOfLadingEpaIdIssues(
    documentId,
    reading?.values.generatorEpaId,
    facilityId
  );
  // Until the reading and the document have both loaded once, the intro, the
  // picker and the grid would each fill in on their own; show one skeleton instead.
  const firstLoad = (readingLoading && !reading) || (fileLoading && !document);

  if (!documentId) {
    return (
      <DocumentDialogContainer open onClose={onClose} title="Review bill of lading" noPrimaryAction>
        <Grid size={{ xs: 12 }}>
          <Alert severity="error">No document to review.</Alert>
        </Grid>
      </DocumentDialogContainer>
    );
  }

  return (
    <DocumentDialogContainer
      open
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      title="Review bill of lading"
      noPrimaryAction
      secondaryActionElements={
        onDiscarded
          ? [
              <BillOfLadingDiscardAction
                key="discard"
                documentId={documentId}
                onDiscarded={onDiscarded}
              />,
            ]
          : undefined
      }
      previewUrl={document?.downloadLink ?? undefined}
      fileExtension={document?.fileExtension ?? undefined}
      previewLoading={fileLoading}
      sx={{ "& .MuiDialog-paper": { height: "calc(100vh - 80px)", maxHeight: "1100px" } }}
      // Caps the form column so its own `overflowY: auto` has a height to
      // scroll inside; without it the whole body scrolled as one and the
      // document could not be moved independently of the fields it is being
      // checked against. The manifest review modal bounds its form the same
      // way. The arithmetic mirrors the dialog height above, less the title and
      // action bars.
      formContainerSx={{ maxHeight: "min(calc(100vh - 220px), 960px)" }}
    >
      <ReviewIntro kinds={kinds} loading={firstLoad} />
      <FacilitySection
        loading={firstLoad}
        tenantId={tenantId}
        value={facility ?? initialFacilityId}
        onChange={setFacility}
        disabled={applying}
        missing={!facilityId}
      />
      {/*
       * The grid ends with its own confirm and cancel buttons, and the dialog
       * body stops immediately under them. Padding here rather than inside the
       * shared grid, which the profile and invoice ingests also render and
       * which sits in their own containers.
       */}
      <Grid size={{ xs: 12 }} sx={{ pb: 3 }}>
        <ExtractionReviewGrid
          schema={BILL_OF_LADING_REVIEW_SCHEMA}
          extraction={reading && !firstLoad ? toExtractionResult(reading) : null}
          status={gridStatus}
          extractionError={gridError}
          onCommit={confirm}
          validator={validate}
          revalidateKey={facilityId}
          persistedIssues={initialIssues}
          warningFieldKeys={WARNING_FIELD_KEYS}
          confirmLabel={isConfirmed ? "Save" : undefined}
          onCancel={onClose}
          onRetry={handleRetry}
          loading={firstLoad}
          committing={applying || readingLoading}
          commitDisabled={!facilityId}
        />
      </Grid>
    </DocumentDialogContainer>
  );
}
